import * as React from "react";
import { Button, Form } from "react-bootstrap";
import { gettext } from "i18n";
import {
  METHOD_BACKUP,
  METHOD_EMAIL,
  METHOD_LABELS,
  METHOD_PASSKEY,
  METHOD_TOTP,
  VerificationMethod,
  passkeysSupported,
} from "utils/mfa";

interface MfaCodeFormProps {
  // The methods the user can verify with, in order of preference
  methods: VerificationMethod[];
  preferred?: VerificationMethod | null;
  emailHint?: string | null;
  // Whether a code has already been emailed (e.g. when email is preferred)
  emailSent?: boolean;
  onRequestEmail: () => Promise<void>;
  // Verifies the code (or the passkey, with no code); resolves to an error
  // message, or null when verified
  onVerify: (
    method: VerificationMethod,
    code?: string,
  ) => Promise<string | null>;
  submitLabel?: string;
}

const CODE_PLACEHOLDERS: Record<VerificationMethod, string> = {
  totp: "123456",
  email: "123456",
  backup: "abcde-fghjk",
  passkey: "",
};

/**
 * Asks for a second factor: a code from the authenticator app, an emailed code,
 * a backup code or a passkey. The user can switch between their methods.
 */
const MfaCodeForm: React.FC<MfaCodeFormProps> = ({
  methods,
  preferred,
  emailHint,
  emailSent = false,
  onRequestEmail,
  onVerify,
  submitLabel,
}) => {
  const usableMethods = methods.filter(
    (method) => method !== METHOD_PASSKEY || passkeysSupported(),
  );
  const initialMethod =
    preferred && usableMethods.includes(preferred)
      ? preferred
      : (usableMethods.find((method) => method !== METHOD_BACKUP) ??
        usableMethods[0]);
  const [method, setMethod] = React.useState<VerificationMethod>(initialMethod);
  const [code, setCode] = React.useState<string>("");
  const [error, setError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState<boolean>(false);
  const [codeSent, setCodeSent] = React.useState<boolean>(emailSent);

  const switchTo = (next: VerificationMethod) => {
    setMethod(next);
    setCode("");
    setError(null);
  };

  const verify = async (verifiedCode?: string) => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const message = await onVerify(method, verifiedCode);
      if (message) {
        setError(message);
        setCode("");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const sendEmail = async () => {
    setError(null);
    try {
      await onRequestEmail();
      setCodeSent(true);
    } catch (e) {
      setError(gettext((e as Error).message));
    }
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    verify(code);
  };

  if (!usableMethods.length) {
    return (
      <p className="text-danger">
        {gettext(
          "None of your sign-in methods works in this browser. Please use another browser, or contact your administrator.",
        )}
      </p>
    );
  }

  return (
    <div className="MfaCodeForm">
      {method === METHOD_PASSKEY ? (
        <>
          <p>
            {gettext(
              "Use your passkey: your fingerprint, face, screen lock or security key.",
            )}
          </p>
          <Button
            className="w-100"
            disabled={submitting}
            onClick={() => verify()}
          >
            {gettext("Use passkey")}
          </Button>
        </>
      ) : (
        <Form onSubmit={onSubmit}>
          <p>
            {method === METHOD_TOTP &&
              gettext("Enter the 6-digit code from your authenticator app.")}
            {method === METHOD_EMAIL &&
              (codeSent
                ? gettext("Enter the code sent to %1.", emailHint ?? "")
                : gettext("We will send a code to %1.", emailHint ?? ""))}
            {method === METHOD_BACKUP &&
              gettext("Enter one of your backup codes. Each code works once.")}
          </p>
          {method === METHOD_EMAIL && !codeSent ? (
            <Button className="w-100" onClick={sendEmail}>
              {gettext("Send code")}
            </Button>
          ) : (
            <>
              <Form.Control
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder={CODE_PLACEHOLDERS[method]}
                aria-label={gettext(METHOD_LABELS[method])}
                autoComplete="one-time-code"
                inputMode={method === METHOD_BACKUP ? "text" : "numeric"}
                maxLength={20}
                className="mb-3"
              />
              <Button
                type="submit"
                className="w-100"
                disabled={submitting || !code.trim()}
              >
                {submitLabel ?? gettext("Verify")}
              </Button>
              {method === METHOD_EMAIL && (
                <Button variant="link" size="sm" onClick={sendEmail}>
                  {gettext("Send a new code")}
                </Button>
              )}
            </>
          )}
        </Form>
      )}
      {error && <p className="text-danger mt-3 mb-0">{error}</p>}
      {usableMethods.length > 1 && (
        <div className="mt-3">
          <small className="text-muted">{gettext("Use another method:")}</small>{" "}
          {usableMethods
            .filter((other) => other !== method)
            .map((other) => (
              <Button
                key={`mfa_method_${other}`}
                variant="link"
                size="sm"
                onClick={() => switchTo(other)}
              >
                {gettext(METHOD_LABELS[other])}
              </Button>
            ))}
        </div>
      )}
    </div>
  );
};

export default MfaCodeForm;
